/**
 * "What a drop's texts are made of" — three short rows in the step rows of
 * Schedule (user, 13/10/2026: buttons and numbers, not sentences): which
 * texts count as proven (the top N of the ranking), what each ORDINARY drop
 * sends (★ proven + ✦ fresh AI texts, on the same drop), and which prompt
 * groups write the fresh texts — every group equally or the best more often,
 * shown as bars of drops per group (15/10/2026) — then the hit-again drops'
 * texts (the bet lines are hidden for now, see `PolicyRows`). The "Today,
 * with this policy" strip and the one-drop example were removed (user,
 * 15/10/2026).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Layers, Sparkles, Star } from 'lucide-react';
import { clampMix, DROP_GEN_TEXT_RANKING_LIMITS as LIMITS, DROP_GEN_TEXTS_PER_DROP_MAX, type DropGenTextMixInput, type DropGenTextRankingConfig } from '@trafficpulse/shared';

import { NumberField } from '@/components/ui';
import { cn } from '@/lib/utils';

import { MINIMAL_FIELD } from '../../../general/controls/field-style';
import { NumberStepper } from '../../../general/controls/NumberStepper';
import { StepRow, ValueLine } from '../../../general/controls/StepRow';
import type { FillExampleInput } from './fill-example';
import type { LeanView } from './lean/lean-rows';
import { LeanPreview } from './lean/LeanPreview';
import { PolicyRows } from './PolicyRows';
import type { TodayFacts } from './today/kind-facts';

const POOLS = [3, 5, 10, 20] as const;

interface Props {
  ranking: DropGenTextRankingConfig;
  onChange: (patch: Partial<DropGenTextRankingConfig>) => void;
  /** The Generation step's AI texts per drop — the same field, shown here too. */
  aiPerDrop: number;
  onAiPerDrop: (amount: number) => void;
  /** Null while the ranking has not loaded — the levers still work. */
  example: FillExampleInput | null;
  /** Phase 37 — today's real drops with this policy; null = no day laid out yet. */
  today?: TodayFacts | null;
  /** The prompt groups the fresh AI texts are shared out between (or the example's). */
  lean: LeanView;
}

export const FillSection: FC<Props> = ({ ranking, onChange, aiPerDrop, onAiPerDrop, example, today = null, lean }) => {
  const cfg: DropGenTextMixInput = { provenPerDrop: ranking.provenPerDrop, policy: ranking.policy, amount: aiPerDrop };
  const setMix = (proven: number, ai: number) => {
    const m = clampMix({ proven, ai });
    if (m.proven !== ranking.provenPerDrop) onChange({ provenPerDrop: m.proven });
    if (m.ai !== aiPerDrop) onAiPerDrop(m.ai);
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="divide-y divide-border/50">
        <StepRow icon={Star} label="Proven texts" hint="Which texts count as proven: the top N of the ranking that can be reused — one SMS long, known to TrafficPulse's catalog, not marked never reuse. A drop that takes proven texts takes the next ones of these in turn, so one text is not sent all day; a pool as big as the number per drop gives every drop the same best texts.">
          <ValueLine>
            <span className="text-xs text-muted-foreground">the top</span>
            <NumberStepper value={ranking.topN} onChange={(v) => onChange({ topN: v })} min={1} max={LIMITS.topNMax} step={1} unit="texts" ariaLabel="Reuse pool size" presets={POOLS} />
            <span className="text-xs text-muted-foreground">of the ranking</span>
          </ValueLine>
        </StepRow>
        <StepRow icon={Layers} label="Each ordinary drop" hint="What one ordinary drop sends, on the same drop: ★ proven texts from the pool and ✦ fresh texts the AI writes — 15 at most together (SMSWiser rotates them). ✦ is the Generation step's AI texts per drop, the same number. If the pool has fewer proven texts than asked, the missing ones become fresh AI texts automatically. The drops that are a bet (below) may send their own mix.">
          <ValueLine>
            <span className="inline-flex items-center gap-1 text-success">
              <Star className="h-4 w-4" aria-hidden />
              <NumberField value={ranking.provenPerDrop} onChange={(v) => setMix(v, aiPerDrop)} min={0} max={DROP_GEN_TEXTS_PER_DROP_MAX} aria-label="Proven texts per ordinary drop" className={cn(MINIMAL_FIELD, 'w-14 text-center')} />
              <span className="text-xs">proven</span>
            </span>
            <span className="text-sm text-muted-foreground">+</span>
            <span className="inline-flex items-center gap-1 text-primary">
              <Sparkles className="h-4 w-4" aria-hidden />
              <NumberField value={aiPerDrop} onChange={(v) => setMix(ranking.provenPerDrop, v)} min={0} max={DROP_GEN_TEXTS_PER_DROP_MAX} aria-label="AI texts per ordinary drop" className={cn(MINIMAL_FIELD, 'w-14 text-center')} />
              <span className="text-xs">fresh AI</span>
            </span>
            <span className="text-xs text-muted-foreground">texts per drop</span>
          </ValueLine>
          {example && example.pool.length < ranking.provenPerDrop ? (
            <p className="text-[11px] text-warning duration-300 animate-in fade-in-0 motion-reduce:animate-none">
              {example.pool.length === 0 ? 'no proven text in the pool yet' : `only ${example.pool.length} proven text${example.pool.length === 1 ? '' : 's'} in the pool`} — the other {ranking.provenPerDrop - example.pool.length} become fresh AI texts automatically
            </p>
          ) : null}
        </StepRow>
        <StepRow icon={Sparkles} label="Fresh AI texts" hint="Each drop's fresh AI texts are written from ONE of this container's prompt groups. Every group equally: the groups take turns, drop after drop. Best groups more often: a group writes for more drops the better its texts score on the ranking's formula — a group without results counts as the average, and every group keeps at least 5 % of the best one's weight so it can still prove itself. The bars show how many drops each group would write.">
          <LeanPreview lean={ranking.leanGroups} onLean={(v) => onChange({ leanGroups: v })} view={lean} aiPerDrop={aiPerDrop} />
        </StepRow>
      </div>
      <div className="rounded-lg border border-border/60 p-3">
        <PolicyRows cfg={cfg} onChange={(policy) => onChange({ policy })} today={today} poolSize={example ? example.pool.length : null} />
      </div>
    </div>
  );
};
